import React, { useState, useRef, useEffect } from 'react';

export interface IConfirmationCodeInput {
  onChange: (value: any) => void;
  isReset?: boolean;
}

const CodeInput = ({ onChange, isReset = false }: IConfirmationCodeInput) => {
  const [code, setCode] = useState<string[]>(['', '', '', '', '', '']);
  const [resetCode, setResetCode] = useState<boolean>(false);
  const inputRefs = useRef<HTMLInputElement[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (isReset) {
      setResetCode(isReset);
    }
  }, [isReset]);

  useEffect(() => {
    if (resetCode) {
      setCode(['', '', '', '', '', '']);
      setResetCode(false);
    }
  }, [resetCode]);

  useEffect(() => {
    onChange(code.join(''));
  }, [code]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const { value } = e.target;
    let g = /^[0-9\b]+$/;

    if (g.test(value) || value === '') {
      setCode((prevCode) => {
        const newCode = [...prevCode];

        // Get the last single digit from the entered value
        const lastDigit = value.match(/[0-9]$/)?.[0];

        // Update the code array with the last digit or an empty string if no digit is provided
        newCode[index] = lastDigit !== undefined ? lastDigit : '';

        // Move focus to the next input if available
        if (index < inputRefs.current.length - 1 && lastDigit !== undefined) {
          inputRefs.current[index + 1]?.focus();
        } else if (index > 0 && lastDigit === undefined) {
          inputRefs.current[index - 1]?.focus();
        }

        return newCode;
      });
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Backspace') {
      const { value } = e.currentTarget;

      if (value === '') {
        e.preventDefault(); // Prevent browser navigation
        const prevIndex = index - 1;

        if (prevIndex >= 0) {
          inputRefs.current[prevIndex]?.focus(); // Focus the previous input field
        }
      }
    }
  };

  const handlePaste = async (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    try {
      const clipboardData =
        e.clipboardData || (await navigator.clipboard.readText());
      const pastedText = clipboardData.getData('text').trim();
      const pastedCharacters = pastedText
        .split('')
        .filter((char) => /^[0-9]+$/.test(char));

      // Create a new array to hold the code values
      const newCode = Array.from({ length: code.length }, (_, index) => {
        return pastedCharacters[index] || ''; // Use pasted characters if available, otherwise empty string
      });

      setCode(newCode);
    } catch (error) {
      console.error('Error reading clipboard data:', error);
    }
  };

  return (
    <>
      <label
        htmlFor="Enter Code"
        className="block text-blue-950 text-sm font-medium font-SF Pro Display leading-none mb-3"
      >
        Enter Code
      </label>
      <div className="flex gap-[10px]">
        {code.map((digit, index) => (
          <input
            key={index}
            type="text"
            placeholder="-"
            className="w-[66px] h-12 p-4 pt-3 bg-white rounded-full border border-zinc-200 justify-center items-center align-middle gap-2.5 inline-flex font-SF Pro Display text-center focus:border-transparent text-blue-950 text-sm font-medium ring-0  placeholder:font-bold focus:ring-lightBlue  hover:border-borderBlue"
            value={digit}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onChange={(e) => handleInputChange(e, index)}
            onPaste={handlePaste} // Add the onPaste event handler
            ref={(ref: any) => (inputRefs.current[index] = ref)}
          />
        ))}
      </div>
    </>
  );
};

export default CodeInput;
